import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  Search, Plus, Building2, Mail, Phone, Globe, MapPin,
  CheckCircle, XCircle, Pencil, Trash2, Filter, RefreshCw,
  Users, Star, X, Save, AlertCircle, ChevronDown, Link2, Calendar
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const COUNTRIES = [
  'Türkiye', 'Almanya', 'İtalya', 'Fransa', 'İspanya', 'ABD', 'Çin', 'Japonya',
  'BAE', 'İngiltere', 'Hollanda', 'İsviçre', 'Avusturya', 'Polonya', 'Rusya', 'Brezilya',
];

const emptyForm = {
  name: '', email: '', phone: '', website: '', country: '', city: '',
  description: '', source: 'Manual', notes: '', associated_fairs: [],
};

// Fair search hook
function FairPicker({ selectedFairs, onChange, token }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!q.trim() || q.length < 2) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/public/fairs?search=${encodeURIComponent(q)}&limit=8`);
        setResults(res.data.items || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 400);
  }, [q]);

  const addFair = (f) => {
    if (!selectedFairs.find(x => x.fair_id === f.fair_id)) {
      onChange([...selectedFairs, { fair_id: f.fair_id, name: f.name, city: f.city }]);
    }
    setQ(''); setResults([]);
  };

  const removeFair = (fair_id) => onChange(selectedFairs.filter(f => f.fair_id !== fair_id));

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Bağlı Fuarlar</label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Fuar adı ara ve ekle..."
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {searching && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />}
        {results.length > 0 && (
          <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-100 max-h-48 overflow-y-auto">
            {results.map(f => (
              <button key={f.fair_id} onClick={() => addFair(f)}
                className="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-sm transition flex items-start gap-2">
                <Calendar className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-gray-800">{f.name}</div>
                  <div className="text-xs text-gray-400">{f.city}, {f.country}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      {selectedFairs.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedFairs.map(f => (
            <span key={f.fair_id} className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
              <Calendar className="w-3 h-3" />
              {f.name}
              <button onClick={() => removeFair(f.fair_id)} className="ml-0.5 hover:text-red-500">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function UltraOrganizers() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [importRunning, setImportRunning] = useState(false);
  const [matchRunning, setMatchRunning] = useState(false);
  const [matchResult, setMatchResult] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchOrganizers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (search) params.set('search', search);
      if (countryFilter) params.set('country', countryFilter);
      const res = await axios.get(`${API}/ultra/organizers?${params}`, { headers });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, countryFilter, token]);

  useEffect(() => { fetchOrganizers(); }, [fetchOrganizers]);

  const openCreate = () => { setEditItem(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({ ...emptyForm, ...item });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editItem) {
        const res = await axios.patch(`${API}/ultra/organizers/${editItem.organizer_id}`, form, { headers });
        setItems(prev => prev.map(i => i.organizer_id === editItem.organizer_id ? res.data : i));
      } else {
        const res = await axios.post(`${API}/ultra/organizers`, form, { headers });
        setItems(prev => [res.data, ...prev]);
        setTotal(t => t + 1);
      }
      setShowModal(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/ultra/organizers/${id}`, { headers });
      setItems(prev => prev.filter(i => i.organizer_id !== id));
      setTotal(t => t - 1);
    } catch (e) { console.error(e); }
    setDeleteId(null);
  };

  const toggleVerified = async (item) => {
    try {
      const res = await axios.patch(`${API}/ultra/organizers/${item.organizer_id}/toggle-verified`, {}, { headers });
      setItems(prev => prev.map(i => i.organizer_id === item.organizer_id ? { ...i, is_verified: res.data.is_verified } : i));
    } catch (e) { console.error(e); }
  };

  const autoMatchFairs = async () => {
    setMatchRunning(true);
    try {
      const res = await axios.post(`${API}/ultra/organizers/auto-match-fairs`, {}, { headers });
      setMatchResult(res.data);
      fetchOrganizers();
    } catch (e) {
      console.error(e);
    } finally {
      setMatchRunning(false);
    }
  };

  const startImport = async (limit = 50) => {
    try {
      setImportRunning(true);
      await axios.post(`${API}/ultra/organizers/import-from-eventseye?limit=${limit}`, {}, { headers });
      // Poll progress
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/ultra/organizers/import-progress`, { headers });
          setImportProgress(res.data);
          if (!res.data.running) {
            clearInterval(poll);
            setImportRunning(false);
            fetchOrganizers();
          }
        } catch { clearInterval(poll); setImportRunning(false); }
      }, 2000);
    } catch (e) {
      setImportRunning(false);
      if (e.response?.status === 409) alert('Import zaten çalışıyor!');
    }
  };

  const kpis = [
    { label: 'Toplam Organizatör', value: total, icon: Building2, color: 'bg-blue-50 text-blue-600' },
    { label: 'Doğrulanmış', value: items.filter(i => i.is_verified).length, icon: CheckCircle, color: 'bg-emerald-50 text-emerald-600' },
    { label: 'Ülke Sayısı', value: new Set(items.map(i => i.country).filter(Boolean)).size, icon: MapPin, color: 'bg-purple-50 text-purple-600' },
    { label: 'Bu Ay Eklenen', value: items.filter(i => i.created_at && i.created_at.startsWith(new Date().toISOString().slice(0, 7))).length, icon: Star, color: 'bg-amber-50 text-amber-600' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="ultra-organizers-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Fuar Organizatörleri</h1>
        <p className="text-gray-500 mt-1">Fuar düzenleyen şirket ve kuruluşları yönet</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpis.map(k => (
          <div key={k.label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${k.color}`}>
                <k.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{k.value.toLocaleString()}</div>
                <div className="text-xs text-gray-500">{k.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Import Progress Bar */}
      {(importRunning || importProgress) && (
        <div className="mb-6 bg-white rounded-2xl p-4 border border-indigo-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-indigo-700">
              <Globe className="w-4 h-4" />
              EventsEye Import {importRunning ? '— Devam Ediyor' : '— Tamamlandı'}
            </div>
            {!importRunning && (
              <button onClick={() => setImportProgress(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {importProgress && (
            <>
              <div className="w-full bg-gray-100 rounded-full h-2 mb-2">
                <div
                  className="bg-indigo-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${importProgress.total > 0 ? Math.round((importProgress.done / importProgress.total) * 100) : 0}%` }}
                />
              </div>
              <div className="flex gap-4 text-xs text-gray-500">
                <span>İşlenen: <b className="text-gray-700">{importProgress.done}/{importProgress.total}</b></span>
                <span>Oluşturulan: <b className="text-emerald-600">{importProgress.created}</b></span>
                {importProgress.errors > 0 && <span>Hata: <b className="text-red-500">{importProgress.errors}</b></span>}
              </div>
            </>
          )}
        </div>
      )}

      {/* Match Result Toast */}
      {matchResult && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">{matchResult.message}</span>
          </div>
          <button onClick={() => setMatchResult(null)} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="İsim, e-posta veya ülke ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="organizer-search"
          />
        </div>
        <select
          value={countryFilter}
          onChange={e => setCountryFilter(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Tüm Ülkeler</option>
          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          onClick={fetchOrganizers}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm hover:bg-gray-50 transition"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-blue-500' : 'text-gray-500'}`} />
        </button>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition"
          data-testid="add-organizer-btn"
        >
          <Plus className="w-4 h-4" /> Organizatör Ekle
        </button>
        <button
          onClick={() => startImport(50)}
          disabled={importRunning}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition"
          title="EventsEye fuar sayfalarından organizatör bilgisi çek"
        >
          {importRunning
            ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> İçe Aktarılıyor...</>
            : <><Globe className="w-4 h-4" /> EventsEye İçe Aktar</>
          }
        </button>
        <button
          onClick={autoMatchFairs}
          disabled={matchRunning}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition"
          title="Organizatörleri şehir/ülkeye göre fuarlarla otomatik eşleştir"
          data-testid="auto-match-btn"
        >
          {matchRunning
            ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Eşleştiriliyor...</>
            : <><Link2 className="w-4 h-4" /> Fuar Eşleştir</>
          }
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Henüz organizatör yok</p>
            <p className="text-sm text-gray-400 mt-1">İlk organizatörü eklemek için butona tıklayın</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Organizatör</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">İletişim</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Konum</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Kaynak</th>
                  <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Durum</th>
                  <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map(item => (
                  <tr key={item.organizer_id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">{item.name}</div>
                          {item.website && (
                            <a href={item.website.startsWith('http') ? item.website : `https://${item.website}`}
                               target="_blank" rel="noreferrer"
                               className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                              <Globe className="w-3 h-3" /> {item.website.replace('https://', '').replace('http://', '').split('/')[0]}
                            </a>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      {item.email && <div className="text-xs text-gray-600 flex items-center gap-1"><Mail className="w-3 h-3" /> {item.email}</div>}
                      {item.phone && <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {item.phone}</div>}
                      {!item.email && !item.phone && <span className="text-xs text-gray-300">—</span>}
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-sm text-gray-700">{item.country || '—'}</div>
                      {item.city && <div className="text-xs text-gray-500">{item.city}</div>}
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        item.source === 'EventsEye' ? 'bg-indigo-50 text-indigo-700' :
                        item.source === 'Manual' ? 'bg-gray-100 text-gray-600' :
                        'bg-amber-50 text-amber-700'
                      }`}>
                        {item.source || 'Manual'}
                      </span>
                      {item.associated_fairs?.length > 0 && (
                        <div className="mt-1 text-xs text-blue-500 flex items-center gap-1">
                          <Link2 className="w-3 h-3" />
                          {item.associated_fairs.length} fuar
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <button
                        onClick={() => toggleVerified(item)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                          item.is_verified
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {item.is_verified
                          ? <><CheckCircle className="w-3.5 h-3.5" /> Doğrulandı</>
                          : <><XCircle className="w-3.5 h-3.5" /> Doğrulanmadı</>}
                      </button>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(item)}
                          className="p-2 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition"
                          data-testid={`edit-organizer-${item.organizer_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteId(item.organizer_id)}
                          className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition"
                          data-testid={`delete-organizer-${item.organizer_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/30 text-sm text-gray-500">
              Toplam {total} organizatörden {items.length} tanesi gösteriliyor
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">
                {editItem ? 'Organizatörü Düzenle' : 'Yeni Organizatör Ekle'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-lg hover:bg-gray-100 transition">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Organizatör Adı *</label>
                  <input
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Messe Frankfurt GmbH"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="organizer-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">E-posta</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="info@messe-frankfurt.com"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Telefon</label>
                  <input
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+49 69 75750"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Web Sitesi</label>
                  <input
                    value={form.website}
                    onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                    placeholder="www.messe-frankfurt.com"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Ülke</label>
                  <select
                    value={form.country}
                    onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Ülke Seçin</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Şehir</label>
                  <input
                    value={form.city}
                    onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                    placeholder="Frankfurt"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Açıklama</label>
                  <textarea
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Organizatör hakkında kısa bilgi..."
                    rows={3}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Kaynak</label>
                  <select
                    value={form.source}
                    onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Manual">Manuel Giriş</option>
                    <option value="EventsEye">EventsEye</option>
                    <option value="Import">İçe Aktarma</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Notlar</label>
                  <input
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="İç notlar..."
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <FairPicker
                  selectedFairs={form.associated_fairs}
                  onChange={fairs => setForm(f => ({ ...f, associated_fairs: fairs }))}
                  token={token}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
              <button
                onClick={() => setShowModal(false)}
                className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition"
                data-testid="save-organizer-btn"
              >
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                {editItem ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Organizatörü Sil</h3>
                <p className="text-sm text-gray-500">Bu işlem geri alınamaz.</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteId(null)} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">İptal</button>
              <button onClick={() => handleDelete(deleteId)} className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700">Sil</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
